import type { CrowdBed } from '@src/audio/CrowdBed';
import { type Sfx, SfxId } from '@src/audio/Sfx';
import { pickEliminationScene, type EliminationScene } from '@src/game/EliminationScenes';

export interface DeathCinematicOptions {
  host: HTMLElement;
  sfx: Sfx;
  crowd: CrowdBed;
  /** Force a named replay (`mop`, `hat`, …). Otherwise a random scene. */
  sceneId?: string;
  onComplete: () => void;
}

const STING_SRC = '/assets/cinematics/death_sting.mp3';
const SKIP_ARM_MS = 450;
const DEFAULT_MS = 9000;
const TAIL_MS = 700;
const VOICE_DELAY_MS = 520;
const STING_DELAY_MS = 80;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  parent?: HTMLElement,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  parent?.appendChild(node);
  return node;
}

/**
 * Broadcast elimination replay: full-bleed video, generated sting + VO, then
 * the existing game-over panel. Space / Enter / click skip after a short arm.
 */
export class DeathCinematic {
  private readonly root: HTMLElement;
  private readonly progressBar: HTMLElement;
  private readonly skipBtn: HTMLButtonElement;
  private readonly sfx: Sfx;
  private readonly crowd: CrowdBed;
  private readonly onComplete: () => void;
  private readonly startTime: number;
  private readonly timers = new Set<number>();
  private voiceAudio: HTMLAudioElement | null = null;
  private stingAudio: HTMLAudioElement | null = null;
  private durationMs = DEFAULT_MS;
  private animFrame = 0;
  private completed = false;
  private armed = false;
  private cleanupListeners: () => void = () => {};

  constructor(opts: DeathCinematicOptions) {
    this.sfx = opts.sfx;
    this.crowd = opts.crowd;
    this.onComplete = opts.onComplete;
    this.startTime = performance.now();
    const scene: EliminationScene = pickEliminationScene(opts.sceneId);

    const root = el('div', 'death-cine', opts.host);
    this.root = root;

    el('div', 'death-cine-letterbox top', root);
    el('div', 'death-cine-letterbox bottom', root);

    const stage = el('div', 'death-cine-stage', root);
    const still = document.createElement('img');
    still.className = 'death-cine-still';
    still.src = `${scene.art}.jpg`;
    still.alt = '';
    still.decoding = 'async';
    still.addEventListener('error', () => still.remove(), { once: true });
    stage.appendChild(still);

    const video = document.createElement('video');
    video.className = 'death-cine-video';
    video.src = `${scene.art}.mp4`;
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('playsinline', '');
    video.addEventListener('loadedmetadata', () => {
      if (video.duration && isFinite(video.duration) && video.duration > 0) {
        this.durationMs = Math.max(this.durationMs, video.duration * 1000);
      }
    });
    video.addEventListener('canplay', () => {
      video.classList.add('ready');
      video.play().catch(() => {});
    });
    video.addEventListener('error', () => {
      video.remove();
    });
    stage.appendChild(video);

    el('div', 'death-cine-scan', stage);
    el('div', 'death-cine-vignette', stage);
    el('div', 'death-cine-grain', stage);
    el('div', 'death-cine-flash', stage);

    const chrome = el('div', 'death-cine-chrome', root);
    const brand = el('div', 'death-cine-brand', chrome);
    el('span', 'death-cine-onair', brand, 'ON AIR');
    el('span', 'death-cine-network', brand, 'ARCANE BROADCAST NETWORK');
    const rec = el('span', 'death-cine-rec', chrome);
    el('i', '', rec);
    rec.appendChild(document.createTextNode(' REC'));
    el('span', 'death-cine-tag', chrome, scene.tag);

    const lower = el('div', 'death-cine-lower', root);
    el('p', 'death-cine-kicker', lower, scene.kicker);
    el('h1', 'death-cine-title', lower, scene.title);
    el('p', 'death-cine-line', lower, scene.line);

    const bottom = el('div', 'death-cine-bottom', root);
    const track = el('div', 'death-cine-progress', bottom);
    this.progressBar = el('i', '', track);
    this.skipBtn = el('button', 'death-cine-skip', bottom);
    this.skipBtn.type = 'button';
    el('span', 'death-cine-skip-key', this.skipBtn, 'SPACE / ENTER / CLICK');
    el('span', 'death-cine-skip-label', this.skipBtn, 'SKIP');

    this.skipBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.requestSkip();
    });
    root.addEventListener('click', () => {
      if (this.armed) this.requestSkip();
    });

    this.bindInputs();
    this.preloadAudio(scene.art);
    this.playBed();
    this.delay(SKIP_ARM_MS, () => {
      this.armed = true;
      this.skipBtn.classList.add('armed');
    });
    this.tick();
  }

  private preloadAudio(art: string): void {
    this.stingAudio = this.loadAudio(STING_SRC, 0.78, (ms) => {
      this.durationMs = Math.max(this.durationMs, STING_DELAY_MS + ms);
    });
    this.voiceAudio = this.loadAudio(`${art}.mp3`, 0.96, (ms) => {
      this.durationMs = Math.max(this.durationMs, VOICE_DELAY_MS + ms);
    });
  }

  private loadAudio(src: string, volume: number, onDuration: (ms: number) => void): HTMLAudioElement | null {
    try {
      const node = new Audio(src);
      node.preload = 'auto';
      node.volume = volume;
      node.addEventListener('loadedmetadata', () => {
        if (node.duration && isFinite(node.duration) && node.duration > 0) {
          onDuration(node.duration * 1000);
        }
      });
      return node;
    } catch {
      return null;
    }
  }

  private playBed(): void {
    this.sfx.play(SfxId.OnAir, 0.95);
    this.delay(STING_DELAY_MS, () => {
      if (this.completed) return;
      this.sfx.play(SfxId.DeathSting, 1);
      this.stingAudio?.play().catch(() => {});
    });
    this.delay(220, () => {
      if (this.completed) return;
      this.sfx.play(SfxId.OrchestraHit, 0.7);
    });
    this.delay(VOICE_DELAY_MS, () => {
      if (this.completed) return;
      this.voiceAudio?.play().catch(() => {});
    });
    // After VO starts so the bed sits under Vaelen, not on the death sting.
    this.delay(VOICE_DELAY_MS + 180, () => {
      if (this.completed) return;
      this.crowd.sting('gasp', 0.7, 0, true);
    });
    this.delay(VOICE_DELAY_MS + 420, () => {
      if (this.completed) return;
      this.crowd.sting('boo', 0.58, 0, true);
    });
  }

  private delay(ms: number, fn: () => void): void {
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      if (!this.completed) fn();
    }, ms);
    this.timers.add(id);
  }

  private tick = (): void => {
    if (this.completed) return;
    const elapsed = performance.now() - this.startTime;
    const total = this.durationMs + TAIL_MS;
    this.progressBar.style.transform = `scaleX(${Math.min(1, elapsed / total)})`;
    if (elapsed >= total) {
      this.finish();
      return;
    }
    this.animFrame = requestAnimationFrame(this.tick);
  };

  private bindInputs(): void {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!this.armed || this.completed) return;
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'Escape' || e.code === 'KeyP') {
        e.preventDefault();
        e.stopPropagation();
        this.requestSkip();
      }
    };

    let pollPad = 0;
    const checkGamepad = () => {
      if (this.completed) return;
      const pads = navigator.getGamepads?.() ?? [];
      for (let i = 0; i < pads.length; i++) {
        const p = pads[i];
        if (!p) continue;
        if (p.buttons[0]?.pressed || p.buttons[1]?.pressed || p.buttons[9]?.pressed) {
          if (this.armed) {
            this.requestSkip();
            return;
          }
        }
      }
      pollPad = requestAnimationFrame(checkGamepad);
    };
    pollPad = requestAnimationFrame(checkGamepad);

    window.addEventListener('keydown', onKeyDown, { capture: true });
    this.cleanupListeners = () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      cancelAnimationFrame(pollPad);
    };
  }

  private requestSkip(): void {
    if (!this.armed || this.completed) return;
    this.finish();
  }

  private stopAudio(node: HTMLAudioElement | null): void {
    if (!node) return;
    try {
      node.pause();
      node.currentTime = 0;
    } catch {
      // audio cleanup
    }
  }

  public finish(): void {
    if (this.completed) return;
    this.completed = true;
    cancelAnimationFrame(this.animFrame);
    for (const id of this.timers) window.clearTimeout(id);
    this.timers.clear();
    this.cleanupListeners();
    this.stopAudio(this.voiceAudio);
    this.stopAudio(this.stingAudio);
    this.voiceAudio = null;
    this.stingAudio = null;

    this.root.classList.add('leaving');
    window.setTimeout(() => {
      this.root.remove();
      this.onComplete();
    }, 280);
  }
}
